import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [resolution, setResolution] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [replyChannel, setReplyChannel] = useState('auto');
  const [replyFiles, setReplyFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const fetchData = async () => {
    try {
      const [ticketRes, notesRes, attRes] = await Promise.all([
        api.get(`/api/tickets/${id}`),
        api.get(`/api/notes/${id}`),
        api.get(`/api/tickets/${id}/attachments`)
      ]);
      setTicket(ticketRes.data);
      setNotes(notesRes.data);
      setAttachments(attRes.data || []);
    } catch (err) {
      console.error('Failed to load ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [id]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    try {
      const { data } = await api.post(`/api/notes/${id}`, { content: newNote });
      setNotes([...notes, data]);
      setNewNote('');
    } catch (err) {
      console.error('Failed to add note:', err);
    }
  };

  const resolveTicket = async (quickResolution) => {
    const finalResolution = quickResolution || resolution.trim() || 'Resolved';
    setResolving(true);
    try {
      const { data } = await api.patch(`/api/tickets/${id}`, { status: 'resolved', resolution: finalResolution });
      setTicket(data);
      setResolution('');
      const notesRes = await api.get(`/api/notes/${id}`);
      setNotes(notesRes.data);
    } catch (err) {
      console.error('Failed to resolve:', err);
    } finally {
      setResolving(false);
    }
  };

  const updatePriority = async (priority) => {
    try {
      const { data } = await api.patch(`/api/tickets/${id}`, { priority });
      setTicket(data);
    } catch (err) {
      console.error('Failed to update priority:', err);
    }
  };

  const reopenTicket = async () => {
    try {
      const { data } = await api.patch(`/api/tickets/${id}`, { status: 'open' });
      setTicket(data);
    } catch (err) {
      console.error('Failed to reopen:', err);
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message', replyMessage);
      formData.append('channel', replyChannel);
      replyFiles.forEach(f => formData.append('attachments', f));
      await api.post(`/api/tickets/${id}/reply`, formData);
      setReplyMessage('');
      setReplyFiles([]);
      await fetchData();
    } catch (err) {
      console.error('Failed to send reply:', err);
      alert('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleReplyPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) setReplyFiles(prev => [...prev, file]);
      }
    }
  };

  const handleReplyDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/') || f.type === 'application/pdf');
    if (files.length) setReplyFiles(prev => [...prev, ...files]);
  };

  const removeReplyFile = (index) => {
    setReplyFiles(prev => prev.filter((_, i) => i !== index));
  };

  const deleteTicket = async () => {
    if (!window.confirm(`Permanently delete ticket #${id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/tickets/${id}`);
      navigate('/');
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('Failed to delete ticket');
    }
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  if (!ticket) return <div className="min-h-screen flex items-center justify-center"><p>Ticket not found</p></div>;

  const priorityColor = { high: 'bg-red-100 text-red-800', medium: 'bg-yellow-100 text-yellow-800', low: 'bg-green-100 text-green-800' };
  const statusColor = { open: 'bg-blue-100 text-blue-800', pending: 'bg-yellow-100 text-yellow-800', resolved: 'bg-green-100 text-green-800' };

  // Separate conversation messages from internal notes/activity
  const conversationTypes = ['email_in', 'email_out', 'sms_in', 'sms_out'];
  const conversation = notes.filter(n => conversationTypes.includes(n.type));
  const internalNotes = notes.filter(n => !conversationTypes.includes(n.type));



  const noteStyle = (type) => {
    switch (type) {
      case 'email_in': return 'bg-blue-50 border-l-4 border-blue-400';
      case 'email_out': return 'bg-indigo-50 border-l-4 border-indigo-400';
      case 'sms_in': return 'bg-green-50 border-l-4 border-green-400';
      case 'sms_out': return 'bg-teal-50 border-l-4 border-teal-400';
      case 'activity': return 'bg-gray-50 border-l-4 border-gray-400';
      default: return 'bg-yellow-50 border-l-4 border-yellow-400';
    }
  };

  const noteLabel = (type) => {
    switch (type) {
      case 'email_in': return '📧 Email received';
      case 'email_out': return '📤 Email sent';
      case 'sms_in': return '💬 SMS received';
      case 'sms_out': return '📱 SMS sent';
      case 'activity': return '⚡ Activity';
      default: return '📝 Note';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="text-blue-600 hover:underline">&larr; Back</button>
          <h1 className="text-lg font-bold">Ticket #{ticket.id}</h1>
          <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor[ticket.status]}`}>{ticket.status}</span>
          <span className={`px-2 py-1 rounded text-xs font-medium ${priorityColor[ticket.priority]}`}>{ticket.priority}</span>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-6 grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="font-bold text-lg mb-1">{ticket.issue_type}</h2>
            {ticket.subject && (
              <p className="text-sm text-gray-500 mb-3 italic">Subject: {ticket.subject}</p>
            )}
            <p className="text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
            {attachments.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium text-gray-500 mb-3">Attachments ({attachments.length})</h4>
                <div className="grid grid-cols-3 gap-3">
                  {attachments.map((a) => {
                    const token = localStorage.getItem('token');
                    const url = `${process.env.REACT_APP_API_URL || ''}/api/tickets/${id}/attachments/${a.id}?token=${token}`;
                    const isImage = (a.content_type || '').startsWith('image/');
                    return (
                      <a key={a.id} href={url} target="_blank" rel="noopener noreferrer"
                        className="block border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                        {isImage ? (
                          <img src={url} alt={a.filename} className="w-full h-32 object-cover bg-gray-50" />
                        ) : (
                          <div className="w-full h-32 bg-gray-50 flex items-center justify-center text-4xl">📎</div>
                        )}
                        <div className="p-2 bg-white">
                          <p className="text-xs font-medium text-gray-700 truncate">{a.filename}</p>
                          <p className="text-xs text-gray-400">{Math.round((a.size || 0) / 1024)}KB</p>
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Send Reply */}
          {(ticket.rep_email || ticket.rep_phone) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold mb-3">Send Reply to Rep</h3>
              <div
                className={`border-2 rounded-lg transition-colors ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleReplyDrop}
              >
                <textarea value={replyMessage} onChange={e => setReplyMessage(e.target.value)}
                  onPaste={handleReplyPaste}
                  placeholder="Write a reply... (paste or drag images here)" rows={3}
                  className="w-full px-3 py-2 rounded-lg resize-none focus:outline-none" />
                {dragOver && (
                  <div className="text-center py-3 text-blue-500 text-sm font-medium">Drop images here</div>
                )}
              </div>
              {replyFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {replyFiles.map((file, i) => (
                    <div key={i} className="relative group">
                      {file.type.startsWith('image/') ? (
                        <img src={URL.createObjectURL(file)} alt={file.name}
                          className="h-16 w-16 object-cover rounded border" />
                      ) : (
                        <div className="h-16 w-16 rounded border bg-gray-50 flex items-center justify-center text-2xl">📎</div>
                      )}
                      <button onClick={() => removeReplyFile(i)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                      <p className="text-xs text-gray-500 truncate w-16 text-center">{file.name}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3 mt-3">
                <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 border border-blue-200 rounded px-3 py-2 hover:bg-blue-50">
                  📎 Attach
                  <input type="file" multiple accept="image/*,.pdf" className="hidden"
                    onChange={e => setReplyFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                </label>
                <select value={replyChannel} onChange={e => setReplyChannel(e.target.value)}
                  className="border rounded px-3 py-2 text-sm">
                  <option value="auto">Auto (match source)</option>
                  {ticket.rep_email && <option value="email">Email only</option>}
                  {ticket.rep_phone && <option value="sms">SMS only</option>}
                </select>
                <button onClick={sendReply} disabled={sending || !replyMessage.trim()}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
                  {sending ? 'Sending...' : `Send Reply${replyFiles.length ? ` (${replyFiles.length} file${replyFiles.length > 1 ? 's' : ''})` : ''}`}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Does not resolve the ticket. Paste, drag & drop, or click Attach to add images.</p>
            </div>
          )}

          {/* Resolve / Re-open */}
          {ticket.status !== 'resolved' ? (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold mb-3">Resolve Ticket</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                <button onClick={() => resolveTicket('Resolved')} disabled={resolving}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 text-sm">
                  {resolving ? 'Resolving...' : 'Quick Resolve'}
                </button>
                <button onClick={() => resolveTicket('Issue corrected — will reflect on next payroll')} disabled={resolving}
                  className="bg-green-100 text-green-800 px-3 py-2 rounded hover:bg-green-200 disabled:opacity-50 text-sm">
                  Corrected — Next Payroll
                </button>
                <button onClick={() => resolveTicket('No action needed — verified correct')} disabled={resolving}
                  className="bg-green-100 text-green-800 px-3 py-2 rounded hover:bg-green-200 disabled:opacity-50 text-sm">
                  Verified Correct
                </button>
              </div>
              <textarea value={resolution} onChange={e => setResolution(e.target.value)}
                placeholder="Or type a custom resolution..." rows={2}
                className="w-full border rounded px-3 py-2 mb-3" />
              <button onClick={() => resolveTicket()} disabled={resolving}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50">
                {resolving ? 'Resolving...' : `Resolve${resolution.trim() ? ' with Message' : ''}`}
              </button>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="font-bold text-green-800 mb-2">Resolution</h3>
              <p className="text-green-700">{ticket.resolution}</p>
              <p className="text-sm text-green-600 mt-2">Resolved by {ticket.resolved_by}</p>
              <button onClick={reopenTicket}
                className="mt-3 text-sm text-blue-600 hover:underline">Re-open ticket</button>
            </div>
          )}

          {/* Conversation Log */}
          {conversation.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold mb-4">Conversation ({conversation.length} messages)</h3>
              <div className="space-y-3">
                {conversation.map(n => (
                  <div key={n.id} className={`p-3 rounded ${noteStyle(n.type)}`}>
                    <div className="flex justify-between text-sm text-gray-500 mb-1">
                      <span className="font-medium">{noteLabel(n.type)} — {n.author}</span>
                      <span>{new Date(n.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{n.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Internal Notes & Activity */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-bold mb-4">Internal Notes & Activity</h3>
            <div className="space-y-3 mb-4">
              {internalNotes.map(n => (
                <div key={n.id} className={`p-3 rounded ${noteStyle(n.type)}`}>
                  <div className="flex justify-between text-sm text-gray-500 mb-1">
                    <span className="font-medium">{n.author}</span>
                    <span>{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-gray-700">{n.content}</p>
                </div>
              ))}
              {internalNotes.length === 0 && <p className="text-gray-400 text-sm">No notes yet</p>}
            </div>
            <form onSubmit={addNote} className="flex gap-2">
              <input value={newNote} onChange={e => setNewNote(e.target.value)}
                placeholder="Add an internal note..." className="flex-1 border rounded px-3 py-2" />
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Add</button>
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-bold text-sm text-gray-500 uppercase mb-3">Details</h3>
            <dl className="space-y-2 text-sm">
              <div><dt className="text-gray-500">Submitted by</dt><dd className="font-medium">{ticket.rep_name}</dd></div>
              {ticket.employee_id && <div><dt className="text-gray-500">Employee ID</dt><dd className="font-medium">{ticket.employee_id}</dd></div>}
              <div><dt className="text-gray-500">Email</dt><dd className="font-medium">{ticket.rep_email || '—'}</dd></div>
              <div><dt className="text-gray-500">Phone</dt><dd className="font-medium">{ticket.rep_phone || '—'}</dd></div>
              {ticket.cc_emails && ticket.cc_emails.length > 0 && (
                <div><dt className="text-gray-500">CC</dt><dd className="font-medium text-xs">{ticket.cc_emails.join(', ')}</dd></div>
              )}
              <div><dt className="text-gray-500">Source</dt><dd className="font-medium capitalize">{ticket.source}</dd></div>
              <div><dt className="text-gray-500">Created</dt><dd className="font-medium">{new Date(ticket.created_at).toLocaleString()}</dd></div>
              <div><dt className="text-gray-500">Updated</dt><dd className="font-medium">{new Date(ticket.updated_at).toLocaleString()}</dd></div>
            </dl>
          </div>

          {ticket.status !== 'resolved' && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-bold text-sm text-gray-500 uppercase mb-3">Priority</h3>
              <div className="flex gap-2">
                {['low', 'medium', 'high'].map(p => (
                  <button key={p} onClick={() => updatePriority(p)}
                    className={`px-3 py-1 rounded text-xs font-medium capitalize ${ticket.priority === p ? priorityColor[p] + ' ring-2 ring-offset-1 ring-gray-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {user.role === 'admin' && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-bold text-sm text-gray-500 uppercase mb-3">Danger Zone</h3>
              <button onClick={deleteTicket}
                className="w-full px-3 py-2 rounded text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 border border-red-200">
                Delete Ticket
              </button>
              <p className="text-xs text-gray-500 mt-2">Admins only. This cannot be undone.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
